/**
 * @file coupon-list.ts
 *
 * Pure display helpers for the Coupons list cells: the discount-type label, the
 * formatted amount (a `%` suffix for percentage coupons, the currency symbol for
 * fixed ones), the usage summary, and the expiry date. Kept separate from the
 * components so the formatting logic stays independently testable without mounting
 * React (mirroring `orders/order-list.ts`); the active/expired status vocabulary
 * lives in `coupon-status.ts`. The page size lives with the API as
 * {@link COUPONS_PER_PAGE}.
 */

import type { MessageDescriptor } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import type { ShopCoupon } from '../api/shop-types';

/** Localized label descriptor per discount type. */
const DISCOUNT_TYPE_LABELS: Record<string, MessageDescriptor> = {
  percent: adminMessages.couponsTypePercent,
  fixed_cart: adminMessages.couponsTypeFixedCart,
  fixed_product: adminMessages.couponsTypeFixedProduct,
};

/**
 * Resolves the localized label descriptor for a discount type, or `undefined`
 * when unrecognised (callers fall back to the raw type string).
 *
 * @param type - The discount type (e.g. `"percent"`).
 *
 * @returns The label {@link MessageDescriptor}, or `undefined`.
 */
export function discountTypeLabel(type: string): MessageDescriptor | undefined {
  return DISCOUNT_TYPE_LABELS[type];
}

/**
 * Formats a coupon's discount amount: a trailing `%` for a percentage coupon, or
 * the shop currency symbol prefixed for a fixed cart/product discount.
 *
 * @param coupon - The coupon.
 * @param currencySymbol - The shop currency symbol (for fixed discounts).
 *
 * @returns The formatted amount string.
 */
export function formatAmount(coupon: ShopCoupon, currencySymbol: string): string {
  return coupon.discount_type === 'percent'
    ? `${coupon.amount}%`
    : `${currencySymbol}${coupon.amount}`;
}

/** Infinity glyph used when a coupon has no usage limit. */
const UNLIMITED = '∞';

/**
 * Formats a coupon's usage as `used / limit`, using an infinity glyph when there
 * is no limit.
 *
 * @param coupon - The coupon.
 *
 * @returns The usage summary (e.g. `"3 / 100"` or `"3 / ∞"`).
 */
export function formatUsage(coupon: ShopCoupon): string {
  const limit = coupon.usage_limit == null ? UNLIMITED : String(coupon.usage_limit);
  return `${coupon.usage_count} / ${limit}`;
}

/**
 * Formats an ISO expiry as a short local date (e.g. `12/31/2026`). Returns a dash
 * for a missing or unparseable value (a coupon that never expires).
 *
 * @param iso - The ISO date string, or `null`.
 *
 * @returns The formatted date, or `—`.
 */
export function formatExpiry(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
